import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  handleClickedButton,
  handleResetButtons,
} from '../../store/slices/settings/editCancelApplyButtonsSlice';
import { selectUserInfo } from '../../store/slices/userSlice';
import {
  getUserProfileDataService,
  updateUserProfileService,
} from '../../../services/userProfile.service';
import { uploadS3File } from '../../../services/uploadS3File.service';
import { isImageFile } from '../../../helpers/helpers';
import { logger } from '../../../helpers/logger';
import {
  describeProfileSaveError,
  getEditableUserProfile,
  userProfileSchema,
} from '../../../helpers/userProfile';
import { joinPhoneValue } from '../../../helpers/countryDialCodes';
import { settingsMessage } from './settingsMessage';

const HONORIFICS = ['male', 'female'];

const INCOMPLETE =
  'Input fields are incomplete. Please fill all required fields.';
const NOT_REFRESHED =
  'Settings were saved, but the current profile could not be refreshed. Reload the page to see the saved values.';

export const profileMessage = (message) =>
  settingsMessage(
    'profile information',
    'change profile information',
    message || 'settings have been applied',
  );

/**
 * The user-profile panel: the form's refs and state, saving it and uploading
 * an avatar. `save` resolves to the message to show; the edit buttons switch
 * to "applied" only once the profile was saved.
 */
export const useSaveUserProfile = () => {
  const dispatch = useDispatch();
  const { user } = useSelector(selectUserInfo);

  const firstNameElement = useRef(null);
  const familyNameElement = useRef(null);
  const titleElement = useRef(null);
  const workPhoneElement = useRef(null);
  const cellPhoneElement = useRef(null);
  const faxElement = useRef(null);
  const emailElement = useRef(null);
  const companyElement = useRef(null);
  const addressElement = useRef(null);

  const [selectHonorific, setSelectHonorific] = useState(null);
  const [workPhoneCountry, setWorkPhoneCountry] = useState(null);
  const [cellPhoneCountry, setCellPhoneCountry] = useState(null);
  const [profileInputsData, setProfileInputsData] = useState(() =>
    getEditableUserProfile(user),
  );
  const [openUploadBox, setOpenUploadBox] = useState(false);

  useEffect(() => {
    setProfileInputsData(getEditableUserProfile(user));
  }, [user]);

  const typedProfile = () => ({
    ...profileInputsData,
    firstname: firstNameElement.current?.value ?? '',
    lastname: familyNameElement.current?.value ?? '',
    title: titleElement.current?.value ?? '',
    gender: HONORIFICS[selectHonorific] ?? '',
    company_name: companyElement.current?.value ?? '',
    company_location: addressElement.current?.value ?? '',
    phone: joinPhoneValue(workPhoneCountry, workPhoneElement.current?.value),
    cell_phone: joinPhoneValue(
      cellPhoneCountry,
      cellPhoneElement.current?.value,
    ),
    fax: faxElement.current?.value ?? '',
    email: emailElement.current?.value ?? '',
  });

  const save = async () => {
    let validatedProfile;
    try {
      validatedProfile = await userProfileSchema.validate(typedProfile(), {
        abortEarly: false,
      });
    } catch (validationError) {
      return profileMessage(validationError?.errors?.[0] || INCOMPLETE);
    }

    setProfileInputsData(validatedProfile);

    try {
      await updateUserProfileService({ user: validatedProfile });
    } catch (error) {
      return profileMessage(describeProfileSaveError(error));
    }

    let refreshMessage;
    try {
      await dispatch(getUserProfileDataService()).unwrap();
    } catch (error) {
      logger.error('Profile was saved but could not be refreshed:', error);
      refreshMessage = NOT_REFRESHED;
    }

    dispatch(handleResetButtons());
    dispatch(handleClickedButton('isApply'));
    return profileMessage(refreshMessage);
  };

  const cancel = () => {
    setProfileInputsData(getEditableUserProfile(user));
    setOpenUploadBox(false);
  };

  const handleUploadImage = async (event, files) => {
    setOpenUploadBox(false);
    const uploadedFile = files?.length > 0 && files[0];
    if (uploadedFile && isImageFile(uploadedFile)) {
      const signed = await uploadS3File(files[0], 'image/png');
      setProfileInputsData((previousProfile) => ({
        ...previousProfile,
        avatar: signed,
      }));
    }
  };

  return {
    panelProps: {
      profileInputsData,
      setProfileInputsData,
      openUploadBox,
      setOpenUploadBox,
      handleUploadImage,
      firstNameElement,
      familyNameElement,
      titleElement,
      workPhoneElement,
      cellPhoneElement,
      faxElement,
      emailElement,
      companyElement,
      addressElement,
      selectHonorific,
      setSelectHonorific,
      workPhoneCountry,
      setWorkPhoneCountry,
      cellPhoneCountry,
      setCellPhoneCountry,
    },
    save,
    cancel,
  };
};
